import { CursorGrid } from "../services/CursorGrid"

import type { Point } from "../types/Point"
import type { KeyboardEvent, PointerEvent, Ref } from "react"

const ARROW_STEPS: Record<string, Point> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
}

interface Props {
  ref?: Ref<HTMLDivElement>
  // The stage's accessible name, which tells how to set the hotspot.
  label: string
  // The 128 px image the hotspot is set on.
  imageUrl: string
  hotspot: Point
  previewBackground?: "transparent" | "light" | "dark"
  onChange: (hotspot: Point) => void
  // Only passed for an animation.
  onPlayToggle?: () => void
}

export const HotspotPicker = ({
  ref,
  label,
  imageUrl,
  hotspot,
  previewBackground = "transparent",
  onChange,
  onPlayToggle,
}: Props) => {
  const pickFromPointer = (event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    onChange({
      x: CursorGrid.clamp(Math.floor(((event.clientX - rect.left) / rect.width) * CursorGrid.SIZE)),
      y: CursorGrid.clamp(Math.floor(((event.clientY - rect.top) / rect.height) * CursorGrid.SIZE)),
    })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === " " && onPlayToggle) {
      event.preventDefault()
      onPlayToggle()
      return
    }

    const step = ARROW_STEPS[event.key]
    if (!step) return

    event.preventDefault()
    const distance = event.shiftKey ? 4 : 1
    onChange({
      x: CursorGrid.clamp(hotspot.x + step.x * distance),
      y: CursorGrid.clamp(hotspot.y + step.y * distance),
    })
  }

  // The crosshair sits on the center of the hotspot pixel.
  const left = `${((hotspot.x + 0.5) / CursorGrid.SIZE) * 100}%`
  const top = `${((hotspot.y + 0.5) / CursorGrid.SIZE) * 100}%`

  return (
    <div
      ref={ref}
      role="group"
      tabIndex={0}
      aria-label={label}
      data-theme={previewBackground === "transparent" ? undefined : previewBackground}
      className={`relative aspect-square w-full cursor-crosshair touch-none ring-1 ring-base-content/20 outline-none focus-visible:ring-3 focus-visible:ring-primary/60 ${
        previewBackground === "transparent" ? "bg-checkerboard" : "bg-base-100"
      }`}
      onPointerDown={(event) => {
        if (event.button !== 0) return

        event.currentTarget.setPointerCapture(event.pointerId)
        pickFromPointer(event)
      }}
      onPointerMove={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) pickFromPointer(event)
      }}
      onKeyDown={handleKeyDown}
    >
      <img
        src={imageUrl}
        alt=""
        className="pointer-events-none block size-full [image-rendering:pixelated]"
      />
      <span className="pointer-events-none absolute inset-x-0 h-px bg-accent/60" style={{ top }} />
      <span className="pointer-events-none absolute inset-y-0 w-px bg-accent/60" style={{ left }} />
      <span
        key={`${hotspot.x}:${hotspot.y}`}
        className="pointer-events-none absolute size-2 -translate-1/2 animate-pulse-ring rounded-full border-2 border-accent"
        style={{ left, top }}
      />
      <span
        className="pointer-events-none absolute size-2.5 -translate-1/2 rounded-full border-2 border-white bg-accent ring-2 ring-accent/30"
        style={{ left, top }}
      />
    </div>
  )
}
